import { PlatformTypes, Post } from "./types";
import { PostMediaFile } from "../media-library/types";
import { parseThreadsData, parseTikTokData } from "./format-post";
import { allowedSelectCoverPlatforms } from "components/Posts/PostForm/components/VideoCoverSelector/const";
import { VIDEO_REEL_CONSTANTS } from "constants/VideoReelConstants";

export type PostApiInput = PostMediaFile & {
  caption: string;
  mediaFiles: PostMediaFile[];
  location: unknown;
  // instagram field
  firstcomment?: string;
  tags?: unknown[];
  productsTags?: [];
  collaboratingIgUsers?: [];
  altText?: string;
  fileName?: string;
  meta?: object;
};

export function getPostDataFromTypedPost(post: Post): PostApiInput {
  // Determine video/reel fields for Facebook

  const isVideoReel =
    post.videoPostType === VIDEO_REEL_CONSTANTS.POST_TYPES.REEL ||
    post.type === "reel";
  const isFbPost =
    post.platforms?.includes("FB") ||
    post.platformType === PlatformTypes.facebook;
  const videoReelData =
    isVideoReel && isFbPost
      ? {
          isFbReel: true,
          type: "fb-reel",
          videoPostType: VIDEO_REEL_CONSTANTS.POST_TYPES.REEL,
        }
      : {};

  const postRootMediaData = parseMediaFileToPostRootData(post);

  const data: Partial<Post> = {
    _id: post._id,
    accountIds: post.accountIds,
    postType: post.state,
    channel: post.channel,
    caption: post.caption || "",
    labelIds: post.labelIds || [],
    postClusterId: post.postClusterId,
    firstcomment: post.firstcomment || "", // required for some reason
    ...postRootMediaData,
    mediaFiles: post.mediaFiles,
    type: isVideoReel && isFbPost ? "fb-reel" : post.type,
    when: post.when,
    // ensure isNow is set to false for drafts or queued posts
    // as they can't be published 'now'
    isNow:
      post.state === "draft" || post.state === "queue" ? false : post.isNow,
    redirectUrl: post.redirectUrl,
    postStatusKey: post.postStatusKey,
    videoPostType: isVideoReel
      ? VIDEO_REEL_CONSTANTS.POST_TYPES.REEL
      : VIDEO_REEL_CONSTANTS.POST_TYPES.VIDEO,
    ...videoReelData,

    ...(post.platformType === PlatformTypes.instagram
      ? {
          tags: post.tags,
          firstcomment: post.firstcomment || "",
          productTags: post.productTags,
          isStory: post.isStory,
          location: post.location,
          storyType: post.storyType,
          storyUrl: post.storyUrl,
          storyLabel: post.storyLabel || null,
          storyStickerHeight: post.storyUrl
            ? post.storyStickerHeight || 0.8
            : null,
          publishStoryManually: post.publishStoryManually,
          collaboratingIgUsers: post.collaboratingIgUsers || [],
        }
      : {}),

    ...(post.platformType === PlatformTypes.facebook
      ? {
          location: post.location,
        }
      : {}),

    ...(post.platformType === PlatformTypes.googlemybusiness
      ? {
          gmbTopicType: post.gmbTopicType,
          gmbEvent: post.gmbEvent,
          gmbOffer: post.gmbOffer,
          gmbCallToAction: post.gmbCallToAction,
        }
      : {}),

    ...(post.platformType === PlatformTypes.linkedin
      ? {
          liTargetEntities: post.liTargetEntities,
          fileName: post.fileName || null,
          meta: post.meta || {},
        }
      : {}),

    ...(post.platformType === PlatformTypes.pinterest
      ? {
          piTitle: post.piTitle,
          piSectionId: post.piSectionId,
          piBoards: post.piBoards,
        }
      : {}),
    ...(post.platformType === PlatformTypes.tiktok
      ? parseTikTokData(post)
      : {}),
    ...(post.platformType === PlatformTypes.threads
      ? parseThreadsData(post)
      : {}),
    ...(post.platformType === PlatformTypes.youtube
      ? {
          ytTitle: post.ytTitle,
          ytPrivacy: post.ytPrivacy,
          ytCategoryId: post.ytCategoryId,
          ytTags: post.ytTags || null,
        }
      : {}),

    ...(post.platformType === PlatformTypes.cluster
      ? {
          tags: post.tags,
          productTags: post.productTags,
          location: post.location,
          gmbTopicType: post.gmbTopicType,
          gmbEvent: post.gmbEvent,
          gmbOffer: post.gmbOffer,
          gmbCallToAction: post.gmbCallToAction,
          piTitle: post.piTitle,
          piSectionId: post.piSectionId,
          piBoards: post.piBoards,
          fileName: post.fileName || null,
          meta: post.meta || postRootMediaData.meta || {},
          thumbnailUrl: post.thumbnailUrl,
          videoCoverImageTimestamp: post?.videoCoverImageTimestamp,
          collaboratingIgUsers: post.collaboratingIgUsers || [],
        }
      : {}),
  };

  if (post.platforms && post.platforms.length > 1) {
    data.platforms = post.platforms;
  } else if (post.platforms) {
    data.platformType = post.platforms[0];
  }

  if (
    post.platforms.some((platform: string) =>
      allowedSelectCoverPlatforms.includes(platform)
    )
  ) {
    const { videoCoverImageTimestamp, thumbnailUrl } = post;

    data.videoCoverImageTimestamp = videoCoverImageTimestamp;

    if (thumbnailUrl) {
      data.thumbnailUrl = thumbnailUrl;

      if (
        Array.isArray(data.mediaFiles) &&
        data.mediaFiles.length > 0 &&
        data.mediaFiles[0].thumbnailUrl
      ) {
        data.mediaFiles[0].thumbnailUrl = thumbnailUrl;
      }
    }
  }

  return data;
}

function parseMediaFileToPostRootData(post: Post): {
  url: string | null;
  originalUrl: string | null;
  origUrl: string | null;
  thumbnailUrl: string | null;
  fileName: string | null;
  isVideo: boolean | null;
  meta: object | null;
  altText: string | null;
} {
  if (post.mediaFiles.length === 0) {
    if (post.url) {
      return {
        url: post.url ?? null,
        originalUrl: post.originalUrl ?? null,
        origUrl: post.originalUrl ?? null,
        thumbnailUrl: post.thumbnailUrl ?? null,
        fileName: post.fileName ?? null,
        isVideo: post.isVideo ?? null,
        meta: post.meta || {},
        altText: post.altText ?? null,
      };
    }
    /** set null to make sure fields are set in BE */
    return {
      url: null,
      originalUrl: null,
      origUrl: null,
      thumbnailUrl: null,
      fileName: null,
      isVideo: false,
      meta: {},
      altText: null,
    };
  }

  const { url, originalUrl, thumbnailUrl, fileName, isVideo, meta, altText } =
    post.mediaFiles[0];

  return {
    url: url ?? null,
    originalUrl: originalUrl ?? null,
    origUrl: originalUrl ?? null,
    thumbnailUrl: thumbnailUrl ?? null,
    fileName: fileName ?? null,
    isVideo: isVideo ?? null,
    meta: meta ?? null,
    altText: altText ?? null,
  };
}

export function normalizePlatformType(platformType: string): number {
  if (typeof platformType === "number") {
    return platformType;
  }

  switch (platformType) {
    case "IG":
      return PlatformTypes.instagram;

    case "FB":
      return PlatformTypes.facebook;

    case "TW":
      return PlatformTypes.twitter;

    case "LI":
      return PlatformTypes.linkedin;

    case "GMB":
      return PlatformTypes.googlemybusiness;

    case "YT":
      return PlatformTypes.youtube;

    case "PI":
      return PlatformTypes.pinterest;

    case "TT":
      return PlatformTypes.tiktok;

    case "TH":
      return PlatformTypes.threads;

    case "SC":
      return PlatformTypes.snapchat;

    case "OT":
      return PlatformTypes.other;

    default:
      return PlatformTypes.instagram;
  }
}
